import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { RevoGrid } from '@revolist/react-datagrid';
import { Button, Spinner, Row, Col } from 'reactstrap';
import * as XLSX from 'xlsx';
import PropTypes from 'prop-types';

/**
 * Componente RevoGridTable
 * Tabla avanzada con virtualización, selección múltiple y exportación a Excel
 */
const RevoGridTable = ({
    columns,
    data,
    loading = false,
    onRowClick,
    onSelectionChange,
    enableSelection = true,
    onExport,
    theme = 'excel',
    resize = true,
    filter = true,
    SearchPlaceholder = 'Buscar...',
    isGlobalFilter = true,
    exportFilename = 'export',
    rowSize = 32,
    height = '500px',
}) => {
    const gridRef = useRef(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedIndexes, setSelectedIndexes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Memoizar datos filtrados
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        const lowerTerm = searchTerm.toLowerCase();
        return data.filter(row => {
            return Object.values(row).some(value => {
                if (value == null) return false;
                return String(value).toLowerCase().includes(lowerTerm);
            });
        });
    }, [searchTerm, data]);

    // Configuración de columnas para RevoGrid
    const gridColumns = useMemo(() => {
        return columns.map(col => ({
            prop: col.prop || col.accessorKey,
            name: col.name || col.header,
            size: col.size || 120,
            sortable: col.sortable !== false,
            filter: filter,
            resize: resize,
            readonly: true,
            // Template personalizado para celdas
            cellTemplate: col.cellTemplate || ((createElement, props) => {
                const value = props.model[props.prop];
                const displayValue = value != null ? String(value) : '-';
                return createElement('span', {
                    className: 'revo-cell-content',
                    title: displayValue,
                }, displayValue);
            }),
        }));
    }, [columns, filter, resize]);

    // Configuración del grid
    const gridConfig = useMemo(() => ({
        stretch: true,
        rowSize: rowSize,
        autoSizeColumn: false,
        range: enableSelection,
        readonly: true,
        canDrag: enableSelection,
    }), [enableSelection, rowSize]);

    // Manejar click en celda/fila
    const handleCellClick = useCallback((e) => {
        if (!e?.detail) return;

        const { model, rowIndex } = e.detail;

        if (onRowClick && model) {
            onRowClick({
                rowData: model,
                rowIndex: rowIndex,
                originalEvent: e,
            });
        }
    }, [onRowClick]);

    // Manejar cambio en selección (range)
    const handleRangeChange = useCallback((e) => {
        if (!enableSelection || !e?.detail) return;

        const range = e.detail;
        let indexes = [];

        // Si hay un rango seleccionado
        if (range && typeof range === 'object') {
            // Manejar selección de rango (shift+click)
            const startRow = range.y || range.rowStart || 0;
            const endRow = range.y1 || range.rowEnd || startRow;

            for (let i = Math.min(startRow, endRow); i <= Math.max(startRow, endRow); i++) {
                indexes.push(i);
            }
        }

        // Obtener datos seleccionados
        const selectedData = indexes
            .map(idx => filteredData[idx])
            .filter(Boolean);

        setSelectedIndexes(indexes);
        setSelectedRows(selectedData);

        if (onSelectionChange) {
            onSelectionChange({
                selectedRows: selectedData,
                selectedIndexes: indexes,
            });
        }
    }, [enableSelection, filteredData, onSelectionChange]);

    // Exportar a Excel
    const handleExport = useCallback(() => {
        const dataToExport = selectedRows.length > 0 ? selectedRows : filteredData;

        if (onExport) {
            onExport(dataToExport);
            return;
        }

        // Exportación por defecto usando xlsx
        if (dataToExport.length === 0) {
            return;
        }

        // Preparar datos para exportación
        const exportData = dataToExport.map(row => {
            const formattedRow = {};
            columns.forEach(col => {
                const key = col.prop || col.accessorKey;
                const header = col.name || col.header;
                formattedRow[header] = row[key] != null ? row[key] : '';
            });
            return formattedRow;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Datos');

        const filename = `${exportFilename}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, filename);
    }, [selectedRows, filteredData, columns, exportFilename, onExport]);

    // Limpiar selección
    const clearSelection = useCallback(() => {
        setSelectedRows([]);
        setSelectedIndexes([]);
        if (gridRef.current) {
            // Limpiar selección en el grid si es posible
        }
        if (onSelectionChange) {
            onSelectionChange({ selectedRows: [], selectedIndexes: [] });
        }
    }, [onSelectionChange]);

    // Estado de carga
    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Cargando datos...</p>
            </div>
        );
    }

    return (
        <div className="revogrid-table-wrapper">
            {/* Barra de herramientas */}
            <Row className="mb-3 align-items-center">
                <Col sm={5}>
                    {isGlobalFilter && (
                        <div className="search-box position-relative">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={SearchPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ paddingLeft: '35px' }}
                            />
                            <i
                                className="ri-search-line position-absolute"
                                style={{
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#878a99'
                                }}
                            />
                        </div>
                    )}
                </Col>
                <Col sm={7} className="text-end">
                    {enableSelection && selectedRows.length > 0 && (
                        <>
                            <span className="me-3 text-muted">
                                <strong>{selectedRows.length}</strong> fila(s) seleccionada(s)
                            </span>
                            <Button
                                color="light"
                                size="sm"
                                className="me-2"
                                onClick={clearSelection}
                            >
                                Limpiar
                            </Button>
                        </>
                    )}
                    <Button color="success" size="sm" onClick={handleExport}>
                        <i className="ri-file-excel-2-line me-1"></i>
                        Exportar Excel
                    </Button>
                </Col>
            </Row>

            {/* Grid */}
            <div
                className={`revogrid-container revogrid-theme-${theme}`}
                style={{
                    border: '1px solid #e9ebec',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <RevoGrid
                    ref={gridRef}
                    columns={gridColumns}
                    source={filteredData}
                    {...gridConfig}
                    onCellClick={handleCellClick}
                    onRangeChange={handleRangeChange}
                    style={{ height: height }}
                />
            </div>

            {/* Footer */}
            <Row className="align-items-center mt-3">
                <Col sm={12}>
                    <div className="text-muted small">
                        Mostrando <strong>{filteredData.length}</strong> de <strong>{data.length}</strong> registros
                        {searchTerm && ` (filtrados por "${searchTerm}")`}
                        {selectedRows.length > 0 && ` | ${selectedRows.length} seleccionados`}
                    </div>
                </Col>
            </Row>

            {/* Estilos personalizados */}
            <style>{`
        .revogrid-theme-excel revo-grid {
          --cell-border: 1px solid #e9ebec;
          --header-bg: #f3f6f9;
          --header-color: #495057;
          --row-hover: #f3f6f9;
          --selection-bg: rgba(64, 153, 255, 0.1);
          --selection-border: #4099ff;
          font-family: inherit;
        }
        
        .revogrid-theme-excel revo-grid .rg-header {
          background: #f3f6f9 !important;
          color: #495057 !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #e9ebec !important;
        }
        
        .revogrid-theme-excel revo-grid .rg-cell {
          border-right: 1px solid #e9ebec !important;
          border-bottom: 1px solid #e9ebec !important;
          font-size: 13px !important;
          color: #212529 !important;
          padding: 4px 8px !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        
        .revogrid-theme-excel revo-grid .rg-cell:focus {
          outline: none !important;
        }
        
        .revogrid-theme-excel revo-grid .rg-row:hover .rg-cell {
          background-color: #f3f6f9 !important;
        }
        
        .revogrid-theme-excel revo-grid .rg-cell.selected,
        .revogrid-theme-excel revo-grid .rg-cell.focused {
          background-color: rgba(64, 153, 255, 0.15) !important;
          border: 2px solid #4099ff !important;
        }
        
        .revogrid-theme-excel revo-grid .rg-focus {
          border: 2px solid #4099ff !important;
        }
        
        /* Scrollbar styling */
        .revogrid-container revo-grid::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        .revogrid-container revo-grid::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .revogrid-container revo-grid::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 5px;
        }
        
        .revogrid-container revo-grid::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
        </div>
    );
};

RevoGridTable.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    onRowClick: PropTypes.func,
    onSelectionChange: PropTypes.func,
    enableSelection: PropTypes.bool,
    onExport: PropTypes.func,
    theme: PropTypes.oneOf(['default', 'excel', 'dark']),
    resize: PropTypes.bool,
    filter: PropTypes.bool,
    SearchPlaceholder: PropTypes.string,
    isGlobalFilter: PropTypes.bool,
    exportFilename: PropTypes.string,
    rowSize: PropTypes.number,
    height: PropTypes.string,
};

export default RevoGridTable;